import React from "react";

import {
  Activity,
  AlertTriangle,
  Banknote,
  BarChart3,
  ClipboardList,
  Coins,
  FolderOpen,
  LayoutDashboard,
  Scale,
  TrendingUp,
  Users
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from "recharts";

import CurrencyDisplay from "../../components/shared/CurrencyDisplay";
import MilestoneGantt from "../../components/shared/MilestoneGantt";
import PageHeader from "../../components/shared/PageHeader";
import { Badge } from "../../components/ui/badge.jsx";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card.jsx";
import { useApi } from "../../context/ApiContext";
import { useAuth } from "../../context/AuthContext";
import { useData } from "../../context/DataContext";
import { useProjectFilter } from "../../hooks/useProjectFilter";
import { formatCurrency, formatIndianNumber } from "../../utils/currencyUtils";

import { useDashboardAnalytics } from "./hooks/useDashboardAnalytics";

const CustomTooltip = ({ active, payload, label, formatter }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white/90 dark:bg-slate-900/90 backdrop-blur-md border border-slate-200 dark:border-slate-800 p-3 rounded-lg shadow-lg z-50">
        {label && <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1">{label}</p>}
        {payload.map((item, index) => (
          <p key={index} className="text-sm font-medium" style={{ color: item.color || item.fill }}>
            {item.name}: {formatter ? formatter(item.value) : item.value}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

const Dashboard = () => {
  const { user } = useAuth();
  const { projects, users, globalProjectId } = useData();
  const { analytics, isLoading: analyticsLoading } = useDashboardAnalytics();
  const { updateProject } = useProjectFilter();
  const api = useApi();
  const navigate = useNavigate();
  const chartColors = ["#f59e0b", "#3b82f6", "#ef4444", "#10b981"];

  const [ganttViewMode, setGanttViewMode] = React.useState("DAILY");
  const [viewingTask, setViewingTask] = React.useState(null);
  const [profitability, setProfitability] = React.useState(null);
  const [isProfitabilityForbidden, setIsProfitabilityForbidden] = React.useState(false);

  React.useEffect(() => {
    const fetchProfitability = async () => {
      try {
        const params = {};
        if (globalProjectId && globalProjectId !== "ALL") {
          params.projectId = globalProjectId;
        }
        const response = await api.get("/projects/profitability", { params });
        setProfitability(response.data);
        setIsProfitabilityForbidden(false);
      } catch (err) {
        console.error("Failed to fetch profitability", err);
        setProfitability(null);
        if (err.response && err.response.status === 403) {
          setIsProfitabilityForbidden(true);
        } else {
          setIsProfitabilityForbidden(false);
        }
      }
    };
    if (user?.permissions?.dashboard?.read) {
      fetchProfitability();
    }
  }, [globalProjectId, api, user]);

  const [permissions, setPermissions] = React.useState({
    canCreate: false,
    canDelete: false,
    canReassign: false,
    canEdit: false
  });

  React.useEffect(() => {
    if (user?.permissions?.task) {
      setPermissions({
        canCreate: user.permissions.task.create,
        canDelete: user.permissions.task.delete,
        canEdit: user.permissions.task.update,
        canReassign: user.permissions.task.update
      });
    }
  }, [user]);

  const isSingleProject = Boolean(globalProjectId && globalProjectId !== "ALL");
  const selectedProject = projects.find((p) => p.id === globalProjectId);
  const activeProjectsCount = projects.filter((p) => p.status === "in_progress").length;

  const formatStatus = (status) => {
    if (!status) return "N/A";
    return status
      .split("_")
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ");
  };

  // Keep completion math aligned with project list: completed=100, in-progress=50, overdue=50, planning/pending=0.
  const calculateTaskCompletionRate = () => {
    if (!analytics) return 0;

    const pendingTasks = Number(analytics.pendingTasks || 0);
    const inProgressTasks = Number(analytics.inProgressTasks || 0);
    const overdueTasks = Number(analytics.overdueTasks || 0);
    const completedTasks = Number(analytics.completedTasks || 0);

    const totalTasks = pendingTasks + inProgressTasks + overdueTasks + completedTasks;
    if (totalTasks === 0) return 0;

    const weightedProgress = completedTasks * 100 + inProgressTasks * 50 + overdueTasks * 50 + pendingTasks * 0;
    return weightedProgress / totalTasks;
  };

  const taskCompletionRate = calculateTaskCompletionRate();
  const overdueCount = Number(analytics?.overdueTasks || 0);

  const totalTasks = analytics
    ? (analytics.completedTasks || 0) +
    (analytics.pendingTasks || 0) +
    (analytics.inProgressTasks || 0) +
    (analytics.overdueTasks || 0)
    : 0;

  const projectResourcesCount = isSingleProject
    ? users.filter(
      (u) => u.allowedProjectIds?.includes(globalProjectId) || u.role === "super_admin" || u.role?.name === "super_admin"
    ).length
    : users.length;

  const totalProjCost = analytics?.totalProjectCost || 0;

  const projectCostData = profitability
    ? [
      {
        name: "Budget",
        value: profitability.budgetedCost || 0,
        color: "#3b82f6"
      },
      {
        name: "Actual Cost",
        value: profitability.actualCost || 0,
        color: "#f59e0b"
      },
      {
        name: "Variance",
        value: (profitability.budgetedCost || 0) - (profitability.actualCost || 0),
        color: (profitability.budgetedCost || 0) - (profitability.actualCost || 0) >= 0 ? "#10b981" : "#ef4444"
      }
    ]
    : [];

  const tasksPieData = analytics
    ? [
      {
        name: "Completed",
        value: analytics.completedTasks || 0,
        color: "#10b981"
      },
      {
        name: "Pending",
        value: analytics.pendingTasks || 0,
        color: "#f59e0b"
      },
      {
        name: "In-Progress",
        value: analytics.inProgressTasks || 0,
        color: "#3b82f6"
      }
    ]
    : [];

  const costByTypeData = analytics?.costByType || [];
  const progressByTypeData = analytics?.progressByType || [];
  const employeeTaskStats = analytics?.employeeTaskStats || [];

  // Navigation handlers for dashboard cards
  const handleActiveProjectsClick = () => {
    navigate("/projects");
  };

  const handleCompletionRateClick = () => {
    navigate("/tasks");
  };

  const handleProfitClick = () => {
    navigate("/payments");
  };

  const handleResourcesClick = () => {
    navigate("/users");
  };

  if (!user?.permissions?.dashboard?.read) {
    return (
      <div className="space-y-8">
        <PageHeader
          title="Project Dashboard"
          subtitle="Track your construction projects in real-time"
          pageIcon={LayoutDashboard}
          notificationCount={overdueCount}
        />

        <div className="flex flex-col items-center justify-center min-h-[400px] text-center p-8 bg-yale-blue-50 dark:bg-yale-blue-900/50 rounded-xl border border-dashed border-slate-200 dark:border-yale-blue-800">
          <div className="bg-slate-200 dark:bg-yale-blue-800 p-4 rounded-full mb-4">
            <BarChart3 className="w-8 h-8 text-slate-400 dark:text-yale-blue-500" />
          </div>
          <h3 className="text-xl font-semibold text-yale-blue-900 dark:text-yale-blue-50 mb-2">Access Restricted</h3>
          <p className="text-yale-blue-500 dark:text-slate-400 max-w-xs mx-auto">
            You don't have permission to view the project dashboard. Please contact your system administrator for access.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <PageHeader
        title="Project Dashboard"
        subtitle="Track your construction projects in real-time"
        pageIcon={LayoutDashboard}
        notificationCount={overdueCount}
      />

      {/* Stats Cards - 4 Premium Items */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6 py-2">
        {/* Card 1: Total Projects */}
        <Card
          className="bg-gradient-to-br from-yale-blue-50 to-blue-100 border-blue-200 dark:from-blue-900/20 dark:to-blue-900/10 dark:border-blue-900/30 cursor-pointer hover:shadow-lg transition-all duration-200 hover:scale-105"
          onClick={handleActiveProjectsClick}
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-yale-blue-600 dark:text-yale-blue-400">
                  Total Projects
                </p>
                <p className="text-3xl font-bold text-blue-800 dark:text-blue-200">
                  {projects.length}
                </p>
                <p className="text-xs text-yale-blue-500 dark:text-yale-blue-400 mt-1">
                  {activeProjectsCount} active
                </p>
              </div>
              <FolderOpen className="h-8 w-8 text-yale-blue-500" />
            </div>
          </CardContent>
        </Card>

        {/* Card 2: Completion Rate */}
        <Card
          className="bg-gradient-to-br from-green-50 to-green-100 border-green-200 dark:from-green-900/20 dark:to-green-900/10 dark:border-green-900/30 cursor-pointer hover:shadow-lg transition-all duration-200 hover:scale-105"
          onClick={handleCompletionRateClick}
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-green-600 dark:text-green-400">Completion Rate</p>
                <p className="text-3xl font-bold text-green-800 dark:text-green-200">{taskCompletionRate.toFixed(0)}%</p>
                <p className="text-xs text-green-500 dark:text-green-400 mt-1">
                  {analytics?.completedTasks || 0} of {totalTasks} tasks completed
                </p>
              </div>
              <TrendingUp className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        {/* Card 3: Profit Percentage */}
        <Card
          className="bg-gradient-to-br from-yale-blue-50 to-amber-100 border-amber-200 dark:from-amber-900/20 dark:to-amber-900/10 dark:border-amber-900/30 cursor-pointer hover:shadow-lg transition-all duration-200 hover:scale-105"
          onClick={handleProfitClick}
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-yale-blue-600 dark:text-yale-blue-400">Profit Percentage</p>
                <p className="text-3xl font-bold text-amber-800 dark:text-amber-200">
                  {isProfitabilityForbidden
                    ? "***"
                    : profitability?.profitability !== undefined
                    ? `${profitability.profitability.toFixed(1)}%`
                    : "0.0%"}
                </p>
                <p className="text-xs text-primary dark:text-yale-blue-400 mt-1">
                  Budget: {isProfitabilityForbidden ? "***" : formatCurrency(profitability?.budgetedCost || 0)}
                </p>
              </div>
              <Banknote className="h-8 w-8 text-primary" />
            </div>
          </CardContent>
        </Card>

        {/* Card 4: Resources */}
        <Card
          className={`bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200 dark:from-purple-900/20 dark:to-purple-900/10 dark:border-purple-900/30 ${user?.permissions?.user?.read ? "cursor-pointer hover:shadow-lg transition-all duration-200 hover:scale-105" : ""
            }`}
          onClick={user?.permissions?.user?.read ? handleResourcesClick : undefined}
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-purple-600 dark:text-purple-400">Resources</p>
                <p className="text-3xl font-bold text-purple-800 dark:text-purple-200">{projectResourcesCount}</p>
                <p className="text-xs text-purple-500 dark:text-purple-400 mt-1">
                  {isSingleProject ? "Assigned to project" : "Total system resources"}
                </p>
              </div>
              <Users className="h-8 w-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Row 2: Financial Overview & Task Status Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="dark:bg-slate-900/40 border-slate-200 dark:border-slate-800 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold flex items-center justify-between">
              <span>Project Cost Overview</span>
              <span className="text-xs font-normal text-slate-500 dark:text-slate-400">Budget vs. Actual vs. Variance</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {projectCostData.length > 0 ? (
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={projectCostData} margin={{ top: 10, right: 10, left: 10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-slate-200 dark:stroke-slate-800" vertical={false} />
                    <XAxis dataKey="name" tickLine={false} className="text-xs fill-slate-500 dark:fill-slate-400" />
                    <YAxis
                      tickLine={false}
                      axisLine={false}
                      tickFormatter={formatIndianNumber}
                      className="text-xs fill-slate-500 dark:fill-slate-400"
                    />
                    <Tooltip content={<CustomTooltip formatter={formatCurrency} />} />
                    <Bar dataKey="value" maxBarSize={60} radius={[6, 6, 0, 0]}>
                      {projectCostData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-[300px] text-slate-400 dark:text-slate-500">
                <Coins className="w-12 h-12 mb-2 stroke-1" />
                <p className="text-sm">
                  {isProfitabilityForbidden ? "Permission denied" : "No cost data available"}
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="dark:bg-slate-900/40 border-slate-200 dark:border-slate-800 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold flex items-center justify-between">
              <span>Task Status Breakdown</span>
              <span className="text-xs font-normal text-slate-500 dark:text-slate-400">Current status distribution</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {tasksPieData.length > 0 && tasksPieData.some((d) => d.value > 0) ? (
              <div className="relative h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={tasksPieData} cx="50%" cy="50%" innerRadius={65} outerRadius={85} paddingAngle={4} dataKey="value">
                      {tasksPieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                    <Legend iconType="circle" className="text-xs" />
                  </PieChart>
                </ResponsiveContainer>
                <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none pb-9">
                  <span className="text-3xl font-extrabold text-slate-800 dark:text-slate-100">
                    {tasksPieData.reduce((acc, curr) => acc + curr.value, 0)}
                  </span>
                  <span className="text-xs font-medium text-slate-400 dark:text-slate-500 uppercase tracking-wider">
                    Total Tasks
                  </span>
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-[300px] text-slate-400 dark:text-slate-500">
                <ClipboardList className="w-12 h-12 mb-2 stroke-1" />
                <p className="text-sm">No tasks created yet</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Row 3: Cost by Type Area Chart & Progress by Category Horizontal Bar Chart */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="dark:bg-slate-900/40 border-slate-200 dark:border-slate-800 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold flex items-center justify-between">
              <span>Cost by Contractor & Material Type</span>
              <span className="text-xs font-normal text-slate-500 dark:text-slate-400">Expenses breakdown</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {costByTypeData.length > 0 ? (
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={costByTypeData} margin={{ top: 10, right: 10, left: 10, bottom: 5 }}>
                    <defs>
                      <linearGradient id="costGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-slate-200 dark:stroke-slate-800" vertical={false} />
                    <XAxis dataKey="name" tickLine={false} className="text-xs fill-slate-500 dark:fill-slate-400" />
                    <YAxis
                      tickLine={false}
                      axisLine={false}
                      tickFormatter={formatIndianNumber}
                      className="text-xs fill-slate-500 dark:fill-slate-400"
                    />
                    <Tooltip content={<CustomTooltip formatter={formatCurrency} />} />
                    <Area type="monotone" dataKey="cost" stroke="#3b82f6" strokeWidth={2} fillOpacity={1} fill="url(#costGrad)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-[300px] text-slate-400 dark:text-slate-500">
                <Scale className="w-12 h-12 mb-2 stroke-1" />
                <p className="text-sm">
                  {isProfitabilityForbidden ? "Permission denied" : "No expenses recorded yet"}
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="dark:bg-slate-900/40 border-slate-200 dark:border-slate-800 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold flex items-center justify-between">
              <span>Progress by Task Category</span>
              <span className="text-xs font-normal text-slate-500 dark:text-slate-400">Average completion %</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {progressByTypeData.length > 0 ? (
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart layout="vertical" data={progressByTypeData} margin={{ top: 10, right: 10, left: 10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-slate-200 dark:stroke-slate-800" horizontal={false} />
                    <XAxis
                      type="number"
                      domain={[0, 100]}
                      tickFormatter={(val) => `${val}%`}
                      tickLine={false}
                      className="text-xs fill-slate-500 dark:fill-slate-400"
                    />
                    <YAxis
                      dataKey="name"
                      type="category"
                      tickLine={false}
                      axisLine={false}
                      width={100}
                      className="text-xs fill-slate-500 dark:fill-slate-400 font-medium"
                    />
                    <Tooltip content={<CustomTooltip formatter={(val) => `${val}%`} />} />
                    <Bar dataKey="progress" fill="#10b981" radius={[0, 6, 6, 0]} maxBarSize={25} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-[300px] text-slate-400 dark:text-slate-500">
                <ClipboardList className="w-12 h-12 mb-2 stroke-1" />
                <p className="text-sm">No task categories found</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Row 4: Gantt Chart / Project Schedule */}
      {isSingleProject && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-xl font-bold text-yale-blue-800 dark:text-slate-100 flex items-center gap-2">
              <Activity className="w-5 h-5 text-yale-blue-500" />
              Project Schedule
            </h2>
          </div>
          <MilestoneGantt viewMode={ganttViewMode} onViewModeChange={setGanttViewMode} isDashboard={true} />
        </div>
      )}

      {/* Row 5: Employee Workload / Stacked Bar Chart */}
      <Card className="dark:bg-slate-900/40 border-slate-200 dark:border-slate-800 shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-semibold flex items-center justify-between">
            <span>Employee Workload & Task Status</span>
            <span className="text-xs font-normal text-slate-500 dark:text-slate-400">Distribution of tasks by status</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {employeeTaskStats.length > 0 ? (
            <div className="h-[350px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={employeeTaskStats} margin={{ top: 10, right: 10, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-slate-200 dark:stroke-slate-800" vertical={false} />
                  <XAxis dataKey="name" tickLine={false} className="text-xs fill-slate-500 dark:fill-slate-400" />
                  <YAxis tickLine={false} axisLine={false} className="text-xs fill-slate-500 dark:fill-slate-400" />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend iconType="circle" className="text-xs" />
                  <Bar dataKey="COMPLETED" stackId="a" fill="#10b981" name="Completed" maxBarSize={45} />
                  <Bar dataKey="IN_PROGRESS" stackId="a" fill="#3b82f6" name="In Progress" maxBarSize={45} />
                  <Bar dataKey="PENDING" stackId="a" fill="#f59e0b" name="Pending" maxBarSize={45} />
                  <Bar dataKey="OVERDUE" stackId="a" fill="#ef4444" name="Overdue" maxBarSize={45} />
                  <Bar dataKey="CANCELLED" stackId="a" fill="#94a3b8" name="Cancelled" maxBarSize={45} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-[350px] text-slate-400 dark:text-slate-500">
              <Users className="w-12 h-12 mb-2 stroke-1" />
              <p className="text-sm">No employee assignment data found</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recent Activities - With Clickable Items */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5 text-purple-500" />
            Recent Activities
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {analytics?.recentActivities?.slice(0, 5).map((activity) => (
              <div
                key={activity.id}
                className="flex items-center gap-4 p-4 bg-yale-blue-50 dark:bg-yale-blue-900/50 rounded-lg hover:bg-slate-100 dark:hover:bg-yale-blue-800 transition-colors cursor-pointer"
                onClick={() => {
                  if (activity.projectId) {
                    updateProject(activity.projectId);
                  }
                  navigate("/activities");
                }}
              >
                <div
                  className={`w-3 h-3 rounded-full ${activity.status ? (activity.status === "completed" ? "bg-green-500" : activity.status === "in_progress" ? "bg-yale-blue-500" : activity.status === "not_started" ? "bg-gray-400" : activity.status === "delayed" ? "bg-red-500" : activity.status === "pending" ? "bg-yellow-500" : activity.status === "cancelled" ? "bg-red-400" : "bg-gray-400") : "bg-gray-400"}`}
                ></div>
                <div className="flex-1">
                  <p className="text-sm text-slate-600 dark:text-slate-400">{`${activity.title} - ${activity.description}`}</p>
                </div>
                <Badge variant="outline" className="dark:border-yale-blue-800 dark:text-slate-300">
                  {activity.status.replace("_", " ")}
                </Badge>
                <div className="text-xs text-slate-400">Click to view project activities →</div>
              </div>
            )) || <p className="text-yale-blue-500 text-center py-8">No recent activities</p>}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Dashboard;
